import { useEffect, useState } from 'react';
import { useStore } from './store';
import { t } from './i18n';
import { stepJalali } from './jalali';
import { Canvas } from './Canvas';
import { NodeModal, AllocateModal, EditNodeModal, SettingsModal, NodeDetailModal, DatePickerModal } from './modals';
import type { FlowNode } from './types';

type ModalState = null | { type: 'payer' | 'receiver' | 'allocate' | 'edit' | 'node'; node?: FlowNode };

export default function App() {
  const lang = useStore((s) => s.lang);
  const theme = useStore((s) => s.theme);
  const date = useStore((s) => s.date);
  const nodes = useStore((s) => s.nodes);
  const allocations = useStore((s) => s.allocations);
  const error = useStore((s) => s.error);
  const loading = useStore((s) => s.loading);
  const bootstrap = useStore((s) => s.bootstrap);
  const setDate = useStore((s) => s.setDate);
  const removeAllocation = useStore((s) => s.removeAllocation);
  const setView = useStore((s) => s.setView);
  const zoom = useStore((s) => s.zoom);

  const [modal, setModal] = useState<ModalState>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showDate, setShowDate] = useState(false);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const onNodeClick = (node: FlowNode) => setModal({ type: 'node', node });
  const onNodeEdit = (node: FlowNode) => setModal({ type: 'edit', node });
  const onNodeHoldDelete = (node: FlowNode) => { useStore.getState().removeNode(node.id); };
  const onAllocEdgeDelete = (sourceId: string, targetId: string) => {
    const ids = allocations.filter((a) => a.sourceNodeId === sourceId && a.targetNodeId === targetId).map((a) => a.id);
    ids.forEach((id) => removeAllocation(id));
  };
  const stepDate = (d: number) => setDate(stepJalali(date, d));
  const zoomBy = (f: number) => setView(Math.min(3, Math.max(0.2, zoom * f)), 0, 0);

  return (
    <div className={`app ${theme}`} dir={lang === 'fa' ? 'rtl' : 'ltr'}>
      <header className="topbar">
        <div className="brand">{t('appTitle', lang)}</div>
        <div className="datebox">
          <button className="btn icon" onClick={() => stepDate(-1)} title={t('back', lang)}>◀</button>
          <button className="dateval datebtn" onClick={() => setShowDate(true)}>{date}</button>
          <button className="btn icon" onClick={() => stepDate(1)} title={t('next', lang)}>▶</button>
        </div>
        <div className="spacer" />
        <button className="btn" onClick={() => setModal({ type: 'payer' })}>{t('addPayer', lang)}</button>
        <button className="btn" onClick={() => setModal({ type: 'receiver' })}>{t('addReceiver', lang)}</button>
        <button className="btn icon" onClick={() => setShowSettings(true)} title={t('settings', lang)}>⚙️</button>
      </header>

      {error && <div className="banner err">{error}</div>}

      <div className="stage">
        {loading && <div className="loading">{lang === 'fa' ? 'در حال بارگذاری…' : 'Loading…'}</div>}
        {!loading && nodes.length === 0 && <div className="empty">{t('empty', lang)}</div>}
        <Canvas onNodeClick={onNodeClick} onNodeEdit={onNodeEdit} onNodeHoldDelete={onNodeHoldDelete} onAllocEdgeDelete={onAllocEdgeDelete} onAddPayer={() => setModal({ type: 'payer' })} onAddReceiver={() => setModal({ type: 'receiver' })} />
      </div>

      {modal?.type === 'payer' && <NodeModal side="payer" onClose={() => setModal(null)} />}
      {modal?.type === 'receiver' && <NodeModal side="receiver" onClose={() => setModal(null)} />}
      {modal?.type === 'allocate' && <AllocateModal preselect={modal.node} onClose={() => setModal(null)} />}
      {modal?.type === 'edit' && modal.node && <EditNodeModal node={modal.node} onClose={() => setModal(null)} />}
      {modal?.type === 'node' && modal.node && (
        <NodeDetailModal
          node={modal.node}
          onClose={() => setModal(null)}
          onEdit={() => setModal({ type: 'edit', node: modal.node })}
        />
      )}
      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
      {showDate && <DatePickerModal value={date} onClose={() => setShowDate(false)} onSelect={(s) => { setDate(s); setShowDate(false); }} />}
    </div>
  );
}
